import {
  Button,
  FormControlLabel,
  TextField,
  Typography,
  Checkbox,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useDispatch, useSelector } from "react-redux";

import { MANUAL, POSTCODE } from "../../../consts/strings";
import { palette } from "../../../theme/palette";

import { setSelectingDriverLocation } from "../../../reducers/driverManagement/thunkActions";

import { View } from "../../View";
import { DriverProfileAddressAutocomplete } from "./DriverProfileAddressAutocomplete";
import { lookupTableSelector } from "../../../reducers/depot/selectors";

const useStyles = makeStyles(() => ({
  column: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "0.75rem",
    marginTop: "1rem",
  },
  formLabel: {
    fontSize: "0.9rem",
  },
  formCheckbox: { paddingLeft: "0.5rem" },
  formCheckboxLabel: {
    color: palette.grey,
    fontSize: "13px",
  },
  yellow: {
    background: palette.btn.yellowFill,
    color: palette.typography.black.high,
    border: palette.btn.yelloBorder,
    boxShadow: "none",
    "&:hover": {
      background: palette.btn.yellowFill,
    },
  },
}));

export const DriverProfileLocationContent = ({
  disabled,
  driverProfileEdit,
  locationType = "start",
  onProfileChange = async () => {},
  onProfileChanges = async () => {},
  errors = {},
}) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const lookupTables = useSelector(lookupTableSelector);

  const {
    [`${locationType}Postcode`]: postcode,
    [`${locationType}Latitude`]: latitude,
    [`${locationType}Longitude`]: longitude,
  } = driverProfileEdit;

  const locationLatitude =
    latitude || lookupTables[POSTCODE][postcode]?.center?.[1];
  const locationLongitude =
    longitude || lookupTables[POSTCODE][postcode]?.center?.[0];

  const [latitudeInt, latitudeDec] = locationLatitude
    ? `${locationLatitude}`.split(".")
    : ["", ""];
  const [longitudeInt, longitudeDec] = locationLongitude
    ? `${locationLongitude}`.split(".")
    : ["", ""];

  const onAddressChange = async ({ address, locationType }) => {
    const toDriverProfileKey = (string) =>
      `${locationType}${string.charAt(0).toUpperCase() + string.slice(1)}`;

    const keys = [
      "isManual",
      "organisationName",
      "addressLine1",
      "addressLine2",
      "city",
      "county",
      "postcode",
      "latitude",
      "longitude",
    ];

    const updates = keys.map((key) => {
      let value;
      if (key === "isManual") value = address.id === MANUAL;
      else value = address[key] || "";
      return {
        name: toDriverProfileKey(key),
        value,
      };
    });

    await onProfileChanges({ updates });
  };

  const alternativeLocation =
    locationType === "end"
      ? "alternativeEndLocation"
      : "alternativeStartLocation";

  return (
    <View>
      <FormControlLabel
        className={styles.formCheckbox}
        control={
          <Checkbox
            color={"default"}
            checked={driverProfileEdit[alternativeLocation]}
            name={alternativeLocation}
            onChange={(e) =>
              onProfileChange({
                ...e,
                target: {
                  ...e.target,
                  name: alternativeLocation,
                  value: !!e.target.checked,
                },
              })
            }
            disabled={disabled}
          />
        }
        label={
          <Typography className={styles.formCheckboxLabel}>
            {`Alternative ${locationType === "end" ? "End" : "Start"} Location`}
          </Typography>
        }
      />
      {driverProfileEdit[alternativeLocation] && (
        <View className={styles.column}>
          <DriverProfileAddressAutocomplete
            name={`${locationType}Location`}
            error={!!errors[`${locationType}Location`]}
            helperText={errors[`${locationType}Location`]}
            onAddressChange={onAddressChange}
            driverProfileEdit={driverProfileEdit}
            disabled={disabled}
            locationType={locationType}
          />
          <TextField
            variant="outlined"
            InputLabelProps={{
              className: styles.formLabel,
              shrink: true,
            }}
            onChange={onProfileChange}
            value={driverProfileEdit[`${locationType}OrganisationName`] || ""}
            name={`${locationType}OrganisationName`}
            label="Organisation Name"
            id={`driver-${locationType}-organistion-name`}
            error={!!errors[`${locationType}OrganisationName`]}
            helperText={errors[`${locationType}OrganisationName`]}
            disabled={disabled || !driverProfileEdit[`${locationType}IsManual`]}
          />
          <TextField
            variant="outlined"
            InputLabelProps={{
              className: styles.formLabel,
              shrink: true,
            }}
            onChange={onProfileChange}
            value={driverProfileEdit[`${locationType}AddressLine1`] || ""}
            name={`${locationType}AddressLine1`}
            label="Address Line 1"
            id={`driver-${locationType}-address-line-1`}
            error={!!errors[`${locationType}AddressLine1`]}
            helperText={errors[`${locationType}AddressLine1`]}
            disabled={disabled || !driverProfileEdit[`${locationType}IsManual`]}
          />
          <TextField
            variant="outlined"
            InputLabelProps={{
              className: styles.formLabel,
              shrink: true,
            }}
            onChange={onProfileChange}
            value={driverProfileEdit[`${locationType}AddressLine2`] || ""}
            name={`${locationType}AddressLine2`}
            label="Address Line 2"
            id={`driver-${locationType}-address-line-2`}
            error={!!errors[`${locationType}AddressLine2`]}
            helperText={errors[`${locationType}AddressLine2`]}
            disabled={disabled || !driverProfileEdit[`${locationType}IsManual`]}
          />
          <TextField
            variant="outlined"
            InputLabelProps={{
              className: styles.formLabel,
              shrink: true,
            }}
            onChange={onProfileChange}
            value={driverProfileEdit[`${locationType}City`] || ""}
            name={`${locationType}City`}
            label={"City"}
            id={`driver-${locationType}-city`}
            error={!!errors[`${locationType}City`]}
            helperText={errors[`${locationType}City`]}
            disabled={disabled || !driverProfileEdit[`${locationType}IsManual`]}
          />
          <TextField
            variant="outlined"
            InputLabelProps={{
              className: styles.formLabel,
              shrink: true,
            }}
            onChange={onProfileChange}
            value={driverProfileEdit[`${locationType}County`] || ""}
            name={`${locationType}County`}
            label={"County"}
            id={`driver-${locationType}-county`}
            error={!!errors[`${locationType}County`]}
            helperText={errors[`${locationType}County`]}
            disabled={disabled || !driverProfileEdit[`${locationType}IsManual`]}
          />
          <TextField
            variant="outlined"
            InputLabelProps={{
              className: styles.formLabel,
              shrink: true,
            }}
            onChange={onProfileChange}
            value={driverProfileEdit[`${locationType}Postcode`] || ""}
            name={`${locationType}Postcode`}
            label={"Postcode"}
            id={`driver-${locationType}-postcode`}
            error={!!errors[`${locationType}Postcode`]}
            helperText={errors[`${locationType}Postcode`]}
            disabled={true}
          />
          <TextField
            variant="outlined"
            InputLabelProps={{
              className: styles.formLabel,
              shrink: true,
            }}
            value={
              latitudeInt && longitudeInt
                ? `${latitudeInt}.${latitudeDec.slice(
                    0,
                    7
                  )}/${longitudeInt}.${longitudeDec.slice(0, 7)}`
                : ""
            }
            name={`${locationType}Coordinates`}
            label={"Coordinates"}
            id={`driver-${locationType}-coordinates`}
            error={!!errors[`${locationType}Coordinates`]}
            helperText={errors[`${locationType}Coordinates`]}
            disabled={true}
          />

          {driverProfileEdit[`${locationType}IsManual`] && (
            <Button
              className={styles.yellow}
              disabled={disabled}
              onClick={() => dispatch(setSelectingDriverLocation(locationType))}
            >
              Select on the Map
            </Button>
          )}
        </View>
      )}
    </View>
  );
};
